import {
  createSlice,
  createAsyncThunk,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { notificationAPI } from "../../services/api";
import type {
  Notification,
  NotificationState,
  NotificationFilters,
  NotificationResponse,
} from "../../types/notifications";

const initialState: NotificationState = {
  notifications: [],
  pagination: null,
  unreadCount: null,
  isUnreadCountLoaded: false,
  loading: false,
  error: null,
  filters: {
    page: 1,
    limit: 20,
    type: "",
    unreadOnly: false,
  },
};

// Async thunks
export const fetchNotifications = createAsyncThunk<
  NotificationResponse,
  NotificationFilters | undefined,
  { rejectValue: string }
>("notifications/fetchNotifications", async (filters, { rejectWithValue }) => {
  try {
    const response = await notificationAPI.getNotifications(filters);
    return response.data.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch notifications"
    );
  }
});

export const fetchUnreadNotifications = createAsyncThunk<
  NotificationResponse,
  { page?: number; limit?: number } | undefined,
  { rejectValue: string }
>(
  "notifications/fetchUnreadNotifications",
  async (params, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.getUnreadNotifications(params);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch unread notifications"
      );
    }
  }
);

export const fetchUnreadCount = createAsyncThunk<
  number,
  void,
  { rejectValue: string; state: { notifications: NotificationState } }
>(
  "notifications/fetchUnreadCount",
  async (_, { rejectWithValue, getState }) => {
    try {
      // Check if count is already loaded to prevent unnecessary API calls
      const state = getState();
      if (
        state.notifications.isUnreadCountLoaded &&
        state.notifications.unreadCount !== null
      ) {
        return state.notifications.unreadCount;
      }

      const response = await notificationAPI.getUnreadCount();
      return response.data.data.unreadCount;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch unread count"
      );
    }
  }
);

export const markNotificationAsRead = createAsyncThunk<
  { notificationId: string },
  string,
  { rejectValue: string }
>("notifications/markAsRead", async (notificationId, { rejectWithValue }) => {
  try {
    await notificationAPI.markAsRead(notificationId);
    return { notificationId };
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to mark notification as read"
    );
  }
});

export const markAllNotificationsAsRead = createAsyncThunk<
  number,
  void,
  { rejectValue: string }
>("notifications/markAllAsRead", async (_, { rejectWithValue }) => {
  try {
    const response = await notificationAPI.markAllAsRead();
    return response.data.data.updatedCount;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message ||
        "Failed to mark all notifications as read"
    );
  }
});

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    setFilters: (
      state,
      action: PayloadAction<Partial<NotificationFilters>>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearNotifications: (state) => {
      state.notifications = [];
      state.pagination = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    // Reset unread count loaded flag to force refresh
    resetUnreadCountCache: (state) => {
      state.isUnreadCountLoaded = false;
      state.unreadCount = null;
    },

    /**
     * A notification pushed over the socket. Shaped identically to a fetched
     * one (the server populates the same related fields before emitting), so it
     * can go straight into the list.
     */
    notificationReceived: (state, action: PayloadAction<Notification>) => {
      const incoming = action.payload;

      // The socket can redeliver on reconnect, and a resync fetch may race the
      // push — either way the same _id must not appear twice.
      if (state.notifications.some((n) => n._id === incoming._id)) return;

      if (state.unreadCount !== null) {
        state.unreadCount += 1;
      }

      // The list currently on screen is filtered. Inserting a notification that
      // doesn't match would show the user a row their own filter excludes, so
      // only the count moves in that case — the item appears when they refetch.
      const { type, unreadOnly } = state.filters;
      if (type && incoming.type !== type) return;
      if (unreadOnly && incoming.isRead) return;

      state.notifications.unshift(incoming);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch notifications
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload.notifications;
        state.pagination = action.payload.pagination;
        state.unreadCount = action.payload.unreadCount;
        state.isUnreadCountLoaded = true;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch notifications";
      })

      // Fetch unread notifications
      .addCase(fetchUnreadNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUnreadNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload.notifications;
        state.pagination = action.payload.pagination;
        state.unreadCount = action.payload.unreadCount;
        state.isUnreadCountLoaded = true;
      })
      .addCase(fetchUnreadNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch unread notifications";
      })

      // Fetch unread count
      .addCase(fetchUnreadCount.pending, (state) => {
        // Don't set loading for count-only requests to avoid UI flicker
        state.error = null;
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
        state.isUnreadCountLoaded = true;
      })
      .addCase(fetchUnreadCount.rejected, (state, action) => {
        state.error = action.payload || "Failed to fetch unread count";
      })

      // Mark as read
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const { notificationId } = action.payload;
        const notification = state.notifications.find(
          (n) => n._id === notificationId
        );
        if (notification && !notification.isRead) {
          notification.isRead = true;
          // Decrease unread count if it exists
          if (state.unreadCount !== null && state.unreadCount > 0) {
            state.unreadCount -= 1;
          }
        }
      })
      .addCase(markNotificationAsRead.rejected, (state, action) => {
        state.error = action.payload || "Failed to mark notification as read";
      })

      // Mark all as read
      .addCase(markAllNotificationsAsRead.fulfilled, (state) => {
        state.notifications.forEach((notification) => {
          notification.isRead = true;
        });
        // Reset unread count to 0
        state.unreadCount = 0;
      })
      .addCase(markAllNotificationsAsRead.rejected, (state, action) => {
        state.error =
          action.payload || "Failed to mark all notifications as read";
      })

      /**
       * Signing in or out throws the whole slice away.
       *
       * This is a correctness fix, not housekeeping. Logging out does not
       * reload the page, so the store survives it — and the bell's own cache
       * flag (`isUnreadCountLoaded`) survives with it. The next user to sign in
       * on that tab mounted the navbar, asked for the count, and was told by
       * `fetchUnreadCount` that it was already loaded: it short-circuits on
       * that flag and returns the value in state. So the bell showed the
       * PREVIOUS user's unread count, and went on showing it until the page was
       * reloaded — which is exactly why refreshing "fixed" it.
       *
       * Two things wrong with that, and the second is the serious one: the
       * count was wrong, and it was another person's. Notifications are
       * per-user (`targetUserId`, and a per-user `readBy`), so a stale count is
       * a small leak across a sign-out on a shared machine.
       *
       * Matched on the action types rather than importing the auth thunks,
       * which would make these two slices import each other. `clearCredentials`
       * is in the list because the axios interceptor signs a revoked session out
       * through it without going near the logout thunk.
       */
      .addMatcher(
        (action: { type: string }) =>
          action.type === "auth/login/fulfilled" ||
          action.type === "auth/pinLogin/fulfilled" ||
          action.type === "auth/logout/fulfilled" ||
          action.type === "auth/logout/rejected" ||
          action.type === "auth/clearCredentials",
        () => initialState,
      );
  },
});

export const {
  setFilters,
  clearNotifications,
  clearError,
  resetUnreadCountCache,
  notificationReceived,
} = notificationSlice.actions;

export default notificationSlice.reducer;
